<template>
    <div class="menu-grid">
        <!-- Controls (Cancel/Save/Create, language selector, visibility toggles)
             live in the parent MenuCollapseItemContent so the editor focuses on
             the phone-preview rendering. -->

        <!-- sr-only aria-live region for navigation announcements -->
        <div role="status" aria-live="polite" class="sr-only">{{ liveRegionMessage }}</div>

        <!-- Phone screen with breadcrumb header + cell body + empty footer -->
        <PhoneScreen
            ref="phone"
            :menu="menu"
            :version="version"
        >
            <!-- HEADER: back-arrow + breadcrumbs (only when inside a submenu) -->
            <template #header="{ ratio, navTextColor }">
                <button
                    v-if="pathUuids.length > 0"
                    ref="backArrow"
                    @click="goBack"
                    class="menu-grid-back-arrow flex-shrink-0 flex items-center cursor-pointer bg-transparent border-0 outline-none p-0"
                    :class="{ 'menu-grid-back-arrow--hover-hold': isBackArrowHoldActive }"
                    :title="$t('apps.configurator.menu-list.back')"
                    :style="{ color: navTextColor, fontSize: `${HEADER_FONT_SIZE * ratio}px` }"
                >
                    <feather-icon icon="ArrowLeftIcon" :style="headerIconStyle(ratio)" />
                </button>
                <nav
                    v-if="pathUuids.length > 0"
                    class="flex items-center min-w-0 flex-1"
                    :style="{ fontSize: `${HEADER_FONT_SIZE * ratio}px`, gap: `${HEADER_GAP * ratio}px` }"
                    aria-label="breadcrumb"
                >
                    <template v-for="(crumb, i) in breadcrumbs">
                        <button
                            v-if="i < breadcrumbs.length - 1"
                            :key="'crumb-' + i"
                            @click="navigateToCrumb(i)"
                            class="hover:underline truncate max-w-xs cursor-pointer bg-transparent border-0 outline-none p-0"
                            :style="{ color: navTextColor }"
                        >{{ crumb.label }}</button>
                        <span v-else :key="'crumb-active-' + i" class="font-semibold truncate max-w-xs" :style="{ color: navTextColor }">{{ crumb.label }}</span>
                        <feather-icon
                            v-if="i < breadcrumbs.length - 1"
                            :key="'sep-' + i"
                            icon="ChevronRightIcon"
                            :style="Object.assign({ color: navTextColor }, headerIconStyle(ratio))"
                        />
                    </template>
                </nav>
            </template>

            <!-- BODY: empty state OR grid of cells -->
            <template #default="{ ratio, containerWidth, fontName }">
                <emptyAlert
                    v-if="displayedItems.length === 0"
                    :message="$t('apps.configurator.alert-empty.item-text')"
                    class="my-4"
                />
                <div
                    v-else
                    class="relative"
                    :style="{ width: `${containerWidth}px` }"
                >
                    <div
                        ref="gridContainer"
                        class="flex flex-wrap"
                        :style="buildGridContainerStyle(layout, ratio)"
                        :role="isNativeMode ? 'list' : 'grid'"
                        @keydown="onGridKeydown"
                    >
                        <div
                            v-for="(item, idx) in displayedItems"
                            :key="item.uuid"
                            :ref="`cell-${item.uuid}`"
                            :data-uuid="item.uuid"
                            :style="cellStyle(item, ratio, fontName)"
                            :class="cellClass(item)"
                            :role="cellRole(item)"
                            :aria-label="cellAriaLabel(item)"
                            :aria-haspopup="cellRole(item) === 'link' ? 'menu' : undefined"
                            :tabindex="idx === focusedIndex ? 0 : -1"
                            @click="onCellClick(item)"
                            @focus="focusedIndex = idx"
                        >
                            <!-- Special item: inline placeholder -->
                            <template v-if="isItemSpecial(item)">
                                <span class="menu-grid-cell__special-title" :style="buildItemTextStyle(styles, ratio, fontName, isNativeMode)">{{ itemLabel(item) }}</span>
                                <code class="menu-grid-cell__special-tag">{{ specialItemTag(item) }}</code>
                            </template>

                            <!-- Regular item text overlay -->
                            <template v-else>
                                <!-- Missing-icon warning (grid mode only — native rows are text-only by design) -->
                                <div
                                    v-if="!isNativeMode && hasMissingIcon(item)"
                                    class="menu-grid-cell__missing-icon"
                                    :title="$t('apps.configurator.menu-list.icon-missing')"
                                    :aria-label="$t('apps.configurator.menu-list.icon-missing')"
                                >
                                    <feather-icon icon="AlertTriangleIcon" :style="warningIconStyle(ratio)" />
                                </div>

                                <!-- Native mode: always show text; fall back to item.name if no title -->
                                <template v-if="isNativeMode">
                                    <div v-if="itemLabel(item) || item.name" :style="buildTextContainerStyle(layout, ratio, isNativeMode)" class="flex justify-center items-end w-full overflow-hidden">
                                        <p :style="buildItemTextStyle(styles, ratio, fontName, isNativeMode)" class="m-0 w-full">{{ itemLabel(item) || item.name }}</p>
                                    </div>
                                </template>
                                <!-- Grid mode: hide text when icon encodes the label or text_height is 0 -->
                                <template v-else>
                                    <!-- Missing title for the selected language: warning instead of falling back to item.name -->
                                    <div
                                        v-if="hasMissingTitle(item)"
                                        :style="buildTextContainerStyle(layout, ratio, isNativeMode)"
                                        class="flex justify-center items-end w-full overflow-hidden"
                                        :title="$t('apps.configurator.menu-list.title-missing')"
                                        :aria-label="$t('apps.configurator.menu-list.title-missing')"
                                    >
                                        <feather-icon icon="AlertTriangleIcon" :style="titleWarningIconStyle(ratio)" />
                                    </div>
                                    <div
                                        v-else-if="itemLabel(item) && !cellHasLanguageVariantIcon(item) && layout.text_height !== 0"
                                        :style="buildTextContainerStyle(layout, ratio, isNativeMode)"
                                        class="flex justify-center items-end w-full overflow-hidden"
                                    >
                                        <p :style="buildItemTextStyle(styles, ratio, fontName, isNativeMode)" class="m-0 w-full">{{ itemLabel(item) }}</p>
                                    </div>
                                </template>
                            </template>

                            <!-- Submenu chip badge: list icon for native (vertical list)
                                 layout, grid icon for home (cell grid) layout. -->
                            <span v-if="isHomeSubmenuCell(item) || isNativeSubmenuCell(item)" class="menu-grid-cell__submenu-chip">
                                <feather-icon
                                    :icon="isNativeSubmenuCell(item) ? 'ListIcon' : 'GridIcon'"
                                    :style="submenuChipIconStyle"
                                />
                            </span>

                            <!-- Invalid-context badge -->
                            <span v-if="isItemInvalidForContext(item)" class="menu-grid-cell__invalid-badge">
                                {{ $t('apps.configurator.menu-list.invalid-context') }}
                            </span>

                            <!-- Drag handle + edit/delete overlay -->
                            <CellHoverActions
                                :read-only="readOnly"
                                @drag-mousedown="onDragHandleMousedown($event, item)"
                                @edit="$emit('editItem', item)"
                                @delete="onDeleteItem(item)"
                            />
                        </div><!-- /cell -->
                    </div><!-- /gridContainer -->

                    <!-- Guide lines overlay (hidden inside a native submenu —
                         the layout there is a vertical list, not a grid). -->
                    <template v-if="showGuides && !isNativeMode">
                        <div
                            v-for="(x, i) in buildGuideLines(layout, ratio, guideTotalRows()).vertical"
                            :key="'gv-' + i"
                            class="absolute top-0 bottom-0 pointer-events-none"
                            :style="{ left: x, width: '1px', zIndex: 5, backgroundImage: dashPattern('bottom') }"
                        />
                        <div
                            v-for="(y, i) in buildGuideLines(layout, ratio, guideTotalRows()).horizontal"
                            :key="'gh-' + i"
                            class="absolute left-0 right-0 pointer-events-none"
                            :style="{ top: y, height: '1px', zIndex: 5, backgroundImage: dashPattern('right') }"
                        />
                    </template>
                </div>
            </template>
        </PhoneScreen>
    </div>
</template>

<script>
import emptyAlert from '@/modules/Shared/Components/alert/emptyAlert.vue'
import PhoneScreen from '@/modules/Apps/Components/configurator/_shared/PhoneScreen.vue'
import CellHoverActions from '@/modules/Apps/Components/configurator/_shared/CellHoverActions.vue'
import dragAndDropMixin from '@/modules/Apps/Components/configurator/_shared/dragAndDropMixin'
import '@/modules/Apps/Components/configurator/_shared/menuGridCell.css'
import {
    isSubmenuItem,
    isNativeSubmenuItem,
    isHomeSubmenuItem,
    isWidgetItem,
    isBannerItem,
    getTemplateKind,
    allowedTemplateKindsForContext,
    widthFor,
    heightFor,
    resolveIconUrl,
    hasLanguageVariantIcon as hasLanguageVariantIconHelper,
    buildCellStyle,
    buildItemTextStyle,
    buildTextContainerStyle,
    buildGridContainerStyle,
    buildGuideLines,
    dashPattern,
    HOVER_HOLD_TO_ENTER_MS,
    CONTEXT_KINDS,
} from '@/modules/Apps/Helpers/menuVisuals'
import {
    HEADER_FONT_SIZE,
    HEADER_GAP,
    HEADER_ICON_SIZE,
} from '@/modules/Apps/Components/configurator/_shared/phoneDimensions'
import {
    getMenuLayout,
    getMenuStyles,
    getMenuResources,
    buildTemplateByUuid,
    buildItemsByUuid,
    findItemParentUuid as findItemParentUuidHelper,
    recomputeOrdering,
} from '@/modules/Apps/Helpers/menuContext'
import { Version } from '@/modules/Apps/Models/Version'

export default {
    name: 'MenuGrid',

    components: { emptyAlert, PhoneScreen, CellHoverActions },

    mixins: [dragAndDropMixin],

    props: {
        menu:                    { type: Object,  required: true },
        version:                 { type: Version, required: true },
        readOnly:                { type: Boolean, default: false },
        showUnpublished:         { type: Boolean, default: true },
        // Controls the dashed grid-guide overlay (parent-owned checkbox).
        showGuides:              { type: Boolean, default: false },
        // Active language for label/icon rendering (parent-owned vs-select).
        selectedLanguageAcronym: { type: String,  default: null },
        editingItemUuid:         { type: String,  default: null },
    },

    data() {
        return {
            pathUuids: [],
            liveRegionMessage: '',
            focusedIndex: 0,
            // Reactive flag mirroring back-arrow hover-hold state — drives the
            // `menu-grid-back-arrow--hover-hold` visual (animated ring around the icon).
            isBackArrowHoldActive: false,
            // Constant aliases for the template (Vue 2 doesn't auto-expose module-level imports)
            HEADER_FONT_SIZE,
            HEADER_GAP,
        }
    },

    computed: {
        // Derived menu context — all the lookups against `version` live in one helper.
        mainLanguage()   { return this.version.mainLanguage() },
        layout()         { return getMenuLayout(this.menu, this.version) },
        styles()         { return getMenuStyles(this.menu, this.version) },
        resources()      { return getMenuResources(this.menu, this.version) },
        languages()      { return this.version.languages },
        templateByUuid() { return buildTemplateByUuid(this.version) },
        // O(1) lookup map covering the entire item tree (replaces recursive walks).
        itemsByUuid()    { return buildItemsByUuid(this.menu) },
        currentItems() {
            let items = this.menu.items
            for (let i = 0; i < this.pathUuids.length; i++) {
                const parent = this.itemsByUuid[this.pathUuids[i]]
                if (!parent) return []
                items = parent.submenu_items || []
            }
            return items
        },
        // Live-preview reordering during drag — operates on full currentItems (incl. unpublished)
        previewCurrentItems() {
            if (!this.draggingItemUuid || !this.hoverTargetUuid) return null
            if (this.draggingItemUuid === this.hoverTargetUuid) return null

            const items = this.currentItems
            const targetIdx = items.findIndex(i => i.uuid === this.hoverTargetUuid)
            if (targetIdx < 0) return null

            const draggingIdx = items.findIndex(i => i.uuid === this.draggingItemUuid)
            const draggingItem = draggingIdx !== -1
                ? items[draggingIdx]
                : this.itemsByUuid[this.draggingItemUuid]
            if (!draggingItem) return null

            const newItems = items.filter(i => i.uuid !== this.draggingItemUuid)
            const adjustedTarget = newItems.findIndex(i => i.uuid === this.hoverTargetUuid)
            if (adjustedTarget < 0) return null
            const insertAt = (draggingIdx !== -1 && draggingIdx < targetIdx)
                ? adjustedTarget + 1
                : adjustedTarget
            newItems.splice(insertAt, 0, draggingItem)
            return newItems
        },
        displayedItems() {
            const items = this.previewCurrentItems || this.currentItems
            return items.filter(i => this.showUnpublished || i.published)
        },
        breadcrumbs() {
            const acronym = this.selectedLanguageAcronym
            const crumbs = [{ label: '#', uuid: null }]
            for (let i = 0; i < this.pathUuids.length; i++) {
                const item = this.itemsByUuid[this.pathUuids[i]]
                if (!item) break
                const titles = item.titles || {}
                const label = (acronym && titles[acronym]) || item.name
                crumbs.push({ label, uuid: item.uuid })
            }
            return crumbs
        },
        isNativeMode() {
            if (this.pathUuids.length === 0) return false
            const parent = this.itemsByUuid[this.pathUuids[this.pathUuids.length - 1]]
            return parent ? isNativeSubmenuItem(parent) : false
        },
        contextKind() {
            if (this.pathUuids.length === 0) return CONTEXT_KINDS.ROOT
            const parent = this.itemsByUuid[this.pathUuids[this.pathUuids.length - 1]]
            return (parent && isNativeSubmenuItem(parent)) ? CONTEXT_KINDS.NATIVE_SUBMENU : CONTEXT_KINDS.HOME_SUBMENU
        },
        // Submenu chip icon size — fixed regardless of phone ratio (the chip
        // is a decorative badge, not part of the in-app content).
        submenuChipIconStyle() {
            return { width: '16px', height: '16px' }
        },
    },

    watch: {
        // Reset internal state when the parent swaps to a different menu without unmounting
        'menu.uuid'() {
            this.pathUuids = []
            this.focusedIndex = 0
            this.liveRegionMessage = ''
        },
        // When an edit session opens (create or edit), bring the target cell
        // into view. For create, the placeholder may be appended below the
        // fold; for edit, clicking a cell already visible is a no-op thanks
        // to `block: 'nearest'`.
        editingItemUuid(uuid) {
            if (!uuid) return
            this.$nextTick(() => {
                if (this.$refs.phone && this.$refs.phone.scrollToCell) {
                    this.$refs.phone.scrollToCell(uuid)
                }
            })
        },
    },

    mounted() {
        // Custom hover-hold timer for back-arrow + submenu entry (additional to mixin's lastHoverCell tracking)
        this.hoverEnterTimerId = null
    },

    beforeDestroy() {
        this.clearHoverTimer()
    },

    methods: {
        // ===== Style builders (re-exported so the template can call them directly) =====
        // Shorthand syntax: each name on the right is the imported pure function.
        // The template calls them with explicit args, avoiding a layer of thin shims.
        buildGridContainerStyle,
        buildItemTextStyle,
        buildTextContainerStyle,
        buildGuideLines,
        dashPattern,

        // ===== Style helpers that genuinely add logic =====

        headerIconStyle(ratio) {
            const size = `${HEADER_ICON_SIZE * ratio}px`
            return { width: size, height: size, flexShrink: '0' }
        },
        // Builds the full cell :style object — large config + iconUrl resolution.
        // eslint-disable-next-line no-unused-vars
        cellStyle(item, ratio, fontName) {
            return buildCellStyle({
                item,
                layout: this.layout,
                ratio,
                isNativeMode: this.isNativeMode,
                isDragging: item.uuid === this.draggingItemUuid,
                isSpecial: this.isItemSpecial(item),
                iconUrl: this.isNativeMode ? null
                    : resolveIconUrl(item, this.resources, this.languages, this.selectedLanguageAcronym),
                isMissingIcon: this.hasMissingIcon(item),
            })
        },
        // Backwards-compatible single-arg form (used by tests and as a fallback).
        // Resolves ratio + fontName from PhoneScreen if available, defaulting to 1.
        getCellStyle(item) {
            const ratio = this.$refs.phone ? this.$refs.phone.ratio : 1
            const fontName = this.$refs.phone ? this.$refs.phone.fontName : null
            return this.cellStyle(item, ratio, fontName)
        },

        // ===== Guides =====

        // Walks displayedItems to count flex-wrap rows consumed at the current
        // columns_number. Required input to buildGuideLines for the horizontal lines.
        guideTotalRows() {
            const cols = this.layout.columns_number || 1
            let rows = this.displayedItems.length > 0 ? 1 : 0
            let used = 0
            for (const item of this.displayedItems) {
                const w = this.effectiveItemWidth(item)
                if (used + w > cols) { rows++; used = w } else { used += w }
            }
            return rows
        },

        // ===== DnD mixin hooks =====

        getDragContainer() {
            return this.$refs.gridContainer
        },
        getCellDimensions(item) {
            const ratio = this.$refs.phone ? this.$refs.phone.ratio : 1
            const rows = item.rows || 1
            const cols = this.isNativeMode ? (this.layout.columns_number || 1) : (item.width || 1)
            return {
                width:  widthFor(cols, this.layout, ratio),
                height: heightFor(rows, this.layout, ratio),
            }
        },
        getCellIconUrl(item) {
            return this.isNativeMode
                ? null
                : resolveIconUrl(item, this.resources, this.languages, this.selectedLanguageAcronym)
        },
        handleEdgeScroll(clientY) {
            if (this.$refs.phone) this.$refs.phone.applyEdgeScroll(clientY)
        },
        // Returns true to short-circuit the mixin's cell-hover handling — used so the
        // back-arrow hover-hold can take precedence over cell hover-hold.
        handlePreHoverChanged(event) {
            const isOverBack = this.isOverBackArrow(event)
            if (isOverBack === this.isBackArrowHoldActive) return isOverBack
            this.isBackArrowHoldActive = isOverBack
            this.clearHoverTimer()
            if (isOverBack) {
                this.scheduleHoverHold(() => {
                    this.isBackArrowHoldActive = false
                    this.goBack()
                })
            }
            return isOverBack
        },
        // Called by the mixin when the hovered cell uuid changes — schedules
        // hover-hold-to-enter when over a home-submenu cell that accepts the dragged item.
        handleHoverChanged(uuid) {
            this.clearHoverTimer()
            if (!uuid) return
            const hoveredItem = this.itemAtCurrentLevel(uuid)
            if (this.shouldHoverHoldEnter(hoveredItem)) {
                this.scheduleHoverHold(() => this.enterSubmenu(hoveredItem))
            }
        },

        // ----- Hover-hold internals -----

        isOverBackArrow(event) {
            return this.pathUuids.length > 0
                && this.isOverElement(this.$refs.backArrow, event.clientX, event.clientY)
        },
        shouldHoverHoldEnter(hoveredItem) {
            return !!hoveredItem
                && isHomeSubmenuItem(hoveredItem)
                && this.canDragInto(this.draggingItemUuid, CONTEXT_KINDS.HOME_SUBMENU)
        },
        // Schedules a HOVER_HOLD_TO_ENTER_MS timer. When it fires, clears the
        // hover-target state and invokes `action`. Replaces previous duplication
        // of the setTimeout block across handlePreHoverChanged + handleHoverChanged.
        scheduleHoverHold(action) {
            this.hoverEnterTimerId = setTimeout(() => {
                this.hoverEnterTimerId = null
                this.hoverTargetUuid = null
                this.lastHoverCell = null
                action()
            }, HOVER_HOLD_TO_ENTER_MS)
        },
        // Resolve a hovered uuid to an item, preferring the current navigation level
        // (so hover-hold only considers submenus at the depth the user is editing).
        // Falls back to the global tree map for cross-level drags.
        itemAtCurrentLevel(uuid) {
            const inLevel = this.currentItems.find(i => i.uuid === uuid)
            return inLevel || this.itemsByUuid[uuid] || null
        },
        // Called by mixin on mouseup
        commitReorder() {
            if (!this.previewCurrentItems) return
            // Capture pre-mutation state for Cmd/Ctrl+Z undo.
            this.pushUndoSnapshot()
            const preview = this.previewCurrentItems
            const items = this.currentItems
            const draggingIdx = items.findIndex(i => i.uuid === this.draggingItemUuid)
            const isCrossLevel = draggingIdx === -1

            if (isCrossLevel) {
                const srcParentUuid = findItemParentUuidHelper(this.draggingItemUuid, this.menu)
                if (srcParentUuid !== undefined) {
                    const srcItems = srcParentUuid === null
                        ? this.menu.items
                        : this.itemsByUuid[srcParentUuid].submenu_items
                    const srcIdx = srcItems.findIndex(i => i.uuid === this.draggingItemUuid)
                    if (srcIdx !== -1) srcItems.splice(srcIdx, 1)
                }
            }

            const destRef = this.getCurrentItemsRef()
            if (destRef) {
                destRef.splice(0, destRef.length, ...preview)
            }
            recomputeOrdering(this.menu.items)

            this.isBackArrowHoldActive = false
            this.clearHoverTimer()
        },

        onDragHandleMousedown(event, item) {
            // Custom state reset (mixin's startDrag handles the rest)
            this.clearHoverTimer()
            this.isBackArrowHoldActive = false
            this.startDrag(event, item)
        },

        // ===== Undo hooks (consumed by the DnD mixin) =====

        getMenuSnapshot() {
            // Deep clone of the full items tree (items have nested submenu_items,
            // so a shallow copy of menu.items is not enough). JSON round-trip is
            // safe — items are plain API-deserialised objects.
            return JSON.parse(JSON.stringify(this.menu.items))
        },
        restoreMenuSnapshot(snapshot) {
            // splice keeps the array identity so Vue's reactivity stays attached.
            this.menu.items.splice(0, this.menu.items.length, ...snapshot)
            recomputeOrdering(this.menu.items)
        },

        // ===== Internal helpers =====

        clearHoverTimer() {
            if (this.hoverEnterTimerId !== null) {
                clearTimeout(this.hoverEnterTimerId)
                this.hoverEnterTimerId = null
            }
        },
        isOverElement(el, clientX, clientY) {
            if (!el) return false
            const r = el.getBoundingClientRect()
            return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom
        },
        getCurrentItemsRef() {
            let items = this.menu.items
            for (let i = 0; i < this.pathUuids.length; i++) {
                const parent = this.itemsByUuid[this.pathUuids[i]]
                if (!parent) return null
                items = parent.submenu_items
            }
            return items
        },
        canDragInto(draggingUuid, targetContextKind) {
            const draggingObj = this.itemsByUuid[draggingUuid]
            if (!draggingObj) return true
            const tmpl = this.templateByUuid[draggingObj.item_template_uuid]
            const isWidget = isWidgetItem(draggingObj) || isWidgetItem(tmpl)
            const isBanner = isBannerItem(draggingObj) || isBannerItem(tmpl)
            if (targetContextKind === CONTEXT_KINDS.HOME_SUBMENU)   return !isWidget
            if (targetContextKind === CONTEXT_KINDS.NATIVE_SUBMENU) return !isWidget && !isBanner
            return true
        },

        // ===== Cell helpers =====

        isItemSpecial(item) {
            const template = this.templateByUuid[item.item_template_uuid]
            return template ? (isWidgetItem(template) || isBannerItem(template)) : false
        },
        isItemInvalidForContext(item) {
            const template = this.templateByUuid[item.item_template_uuid]
            if (!template) return false
            const allowed = allowedTemplateKindsForContext(this.contextKind)
            return !allowed[getTemplateKind(template)]
        },
        isHomeSubmenuCell(item) {
            return isSubmenuItem(item) && isHomeSubmenuItem(item)
        },
        isNativeSubmenuCell(item) {
            return isSubmenuItem(item) && isNativeSubmenuItem(item)
        },
        itemLabel(item) {
            // Banners and widgets are language-agnostic config blocks — they
            // identify themselves by `name`, not by localised `titles`.
            if (this.isItemSpecial(item)) return item.name || ''
            const acronym = this.selectedLanguageAcronym
            const titles = item.titles || {}
            return (acronym && titles[acronym]) || item.name || ''
        },
        cellHasLanguageVariantIcon(item) {
            return hasLanguageVariantIconHelper(item, this.resources, this.languages)
        },
        // True when a regular cell can't render an icon — either because item.icon
        // is empty/unset, or because it's set but the resource doesn't exist in the CDN.
        // Both states are flagged the same way: the editor sees a broken cell and
        // knows to fix it.
        //
        // Doesn't apply to special items (widgets/banners have their own placeholder)
        // or to native-mode rows (text-only by design — caller already guards via
        // !isNativeMode in the template).
        hasMissingIcon(item) {
            if (!item) return false
            if (this.isItemSpecial(item)) return false
            // resolveIconUrl returns null when item.icon is falsy OR no resource matches
            return !resolveIconUrl(item, this.resources, this.languages, this.selectedLanguageAcronym)
        },
        // True when a regular home cell would show text but the selected
        // language has no title — surfaces as a warning icon in place of the
        // text (no name fallback). Doesn't fire in native mode, on special
        // items, when the layout hides text, or when the icon itself encodes
        // the label via language variants.
        hasMissingTitle(item) {
            if (!item) return false
            if (this.isNativeMode) return false
            if (this.isItemSpecial(item)) return false
            if (this.layout && this.layout.text_height === 0) return false
            if (this.cellHasLanguageVariantIcon(item)) return false
            const acronym = this.selectedLanguageAcronym
            if (!acronym) return false
            const titles = item.titles || {}
            return !titles[acronym]
        },
        warningIconStyle(ratio) {
            const size = `${28 * ratio}px`
            return { width: size, height: size, color: 'rgba(220,38,38,0.95)' }
        },
        titleWarningIconStyle(ratio) {
            const size = `${18 * ratio}px`
            return { width: size, height: size, color: 'rgba(220,38,38,0.95)' }
        },
        specialItemTag(item) {
            const template = this.templateByUuid[item.item_template_uuid]
            return template && isBannerItem(template) ? 'banner' : 'widget'
        },
        effectiveItemWidth(item) {
            if (this.isNativeMode || this.isItemSpecial(item)) return this.layout.columns_number || 1
            return item.width || 1
        },
        cellClass(item) {
            return {
                'menu-grid-cell': true,
                'menu-grid-cell--unpublished': !item.published,
                'menu-grid-cell--submenu': isSubmenuItem(item),
                'menu-grid-cell--native': isNativeSubmenuItem(item),
                'menu-grid-cell--special': this.isItemSpecial(item),
                'menu-grid-cell--dragging': item.uuid === this.draggingItemUuid,
                'menu-grid-cell--hover-hold': item.uuid === this.hoverTargetUuid && this.hoverEnterTimerId !== null,
                'menu-grid-cell--drop-target': item.uuid === this.hoverTargetUuid && this.hoverEnterTimerId === null,
                'menu-grid-cell--editing': item.uuid === this.editingItemUuid,
                'menu-grid-cell--missing-icon': this.hasMissingIcon(item),
            }
        },
        cellRole(item) {
            return isSubmenuItem(item) ? 'link' : 'button'
        },
        cellAriaLabel(item) {
            const label = this.itemLabel(item)
            return !item.published ? `${label} (unpublished)` : label
        },

        // ===== Navigation =====

        onCellClick(item) {
            // The post-drag synthetic click is intercepted by the mixin at the
            // document level (capture phase), so we never see it here.
            if (isSubmenuItem(item)) this.enterSubmenu(item)
        },
        onDeleteItem(item) {
            this.$vs.dialog({
                type: 'confirm',
                color: 'danger',
                title: this.$t('apps.configurator.menu-list.remove.title'),
                text:  this.$t('apps.configurator.menu-list.remove.text'),
                accept: () => this.$emit('deleteItem', item),
            })
        },
        enterSubmenu(item) {
            this.pathUuids.push(item.uuid)
            this.focusedIndex = 0
            this.liveRegionMessage = this.$t('apps.configurator.menu-list.entered', [this.itemLabel(item)])
            this.$emit('navigationChanged', this.pathUuids.slice())
            this.scrollGridToTop()
        },
        goBack() {
            if (this.pathUuids.length === 0) return
            this.pathUuids.pop()
            this.focusedIndex = 0
            const last = this.breadcrumbs[this.breadcrumbs.length - 1]
            this.liveRegionMessage = this.$t('apps.configurator.menu-list.returned-to', [last ? last.label : ''])
            this.$emit('navigationChanged', this.pathUuids.slice())
            this.scrollGridToTop()
        },
        navigateToCrumb(index) {
            this.pathUuids = this.pathUuids.slice(0, index)
            this.focusedIndex = 0
            this.$emit('navigationChanged', this.pathUuids.slice())
        },
        scrollGridToTop() {
            this.$nextTick(() => {
                if (this.$refs.phone) this.$refs.phone.scrollToTop()
            })
        },

        // ===== Keyboard navigation =====

        onGridKeydown(event) {
            const count = this.displayedItems.length
            if (count === 0) return
            const cols = this.isNativeMode ? 1 : (this.layout.columns_number || 1)

            const moveTo = (newIdx) => {
                if (newIdx < 0 || newIdx >= count) return
                this.focusedIndex = newIdx
                this.$nextTick(() => {
                    const item = this.displayedItems[newIdx]
                    const refArr = item && this.$refs[`cell-${item.uuid}`]
                    const el = Array.isArray(refArr) ? refArr[0] : refArr
                    if (el && el.focus) el.focus()
                })
            }

            const key = event.key
            if (key === 'ArrowRight' && !this.isNativeMode) {
                event.preventDefault()
                moveTo(Math.min(this.focusedIndex + 1, count - 1))
            } else if (key === 'ArrowLeft' && !this.isNativeMode) {
                event.preventDefault()
                moveTo(Math.max(this.focusedIndex - 1, 0))
            } else if (key === 'ArrowDown') {
                event.preventDefault()
                moveTo(Math.min(this.focusedIndex + cols, count - 1))
            } else if (key === 'ArrowUp') {
                event.preventDefault()
                moveTo(Math.max(this.focusedIndex - cols, 0))
            } else if (key === 'Enter') {
                event.preventDefault()
                const item = this.displayedItems[this.focusedIndex]
                if (!item) return
                if (isSubmenuItem(item)) this.enterSubmenu(item)
                else if (!this.readOnly) this.$emit('editItem', item)
            } else if (key === 'Escape') {
                event.preventDefault()
                if (this.pathUuids.length > 0) this.goBack()
            }
        },
    },
}
</script>
